import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Target, Plus, Pause, Play, Trophy, Pencil, Check, X, Sparkles, Loader2 } from 'lucide-react';
import { PFP } from './styles';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const errMsg = (e, fb) => (typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : fb);

export const GoalBadge = ({ source }) => (
  <span
    className={`inline-flex items-center text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full ${
      source === 'parent' ? 'bg-[#FDE7D8] text-[#9A4F22]' : 'bg-[#DCEBF7] text-[#24557A]'
    }`}
    data-testid={`goal-badge-${source}`}
  >
    {source === 'parent' ? 'Parent Goal' : 'Therapist Goal'}
  </span>
);

const INTRO = "Your child's goals help us make their daily play ideas even more personal. You can choose goals that matter to your family, and if your child's therapist has shared goals, you'll see those here too. We'll weave these goals into some of your play ideas while continuing to support learning and development through everyday play.";

function HowGoalsShapePlay() {
  return (
    <section className={`${PFP.card} p-5 bg-gradient-to-br from-[#FFF8E1]/80 to-white`} data-testid="how-goals-shape-playtime">
      <h2 className="font-serif text-xl text-[#3a2f4f] mb-2">How Your Goals Shape Playtime</h2>
      <div className="space-y-2 text-sm text-stone-700 leading-relaxed">
        <p>Your child&apos;s goals help guide some of the play ideas we send you. We use your chosen goals, any goals shared by your therapist, and your child&apos;s Language Map to create playful opportunities for communication and learning.</p>
        <p>Not every activity will focus on a specific goal — and that&apos;s intentional! We also include a variety of child-led play ideas to support your child&apos;s overall development.</p>
        <p>When an activity supports one of your goals, we&apos;ll show you exactly which goal it relates to and how the activity helps.</p>
      </div>
    </section>
  );
}

function TherapistGoals({ goals }) {
  return (
    <section className={`${PFP.card} p-5`} data-testid="therapist-goals-section">
      <h2 className="font-serif text-xl text-[#3a2f4f] mb-1">Goals From Your Therapist</h2>
      <p className="text-xs text-stone-500 mb-3">Shared by your child&apos;s therapist to support play at home. Only your therapist can change these.</p>
      {goals.length === 0 && <p className="text-sm text-stone-600" data-testid="therapist-goals-empty">No goals have been shared by your therapist yet.</p>}
      <ul className="space-y-2">
        {goals.map((g) => (
          <li key={g.title} className="rounded-2xl border border-[#DCEBF7] bg-[#F5FAFE] p-3" data-testid="therapist-goal-item">
            <div className="flex items-center gap-2 flex-wrap mb-1"><GoalBadge source="therapist" /><span className="text-[11px] text-stone-500">Current focus</span></div>
            <p className="text-sm text-stone-900">{g.title}</p>
            {g.therapist_name && <p className="text-[11px] text-stone-500 mt-0.5">Shared by {g.therapist_name}</p>}
          </li>
        ))}
      </ul>
    </section>
  );
}

function ParentGoalItem({ g, onPatch }) {
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(g.title);
  const paused = g.status === 'paused';
  return (
    <li className={`rounded-2xl border p-3 ${paused ? 'border-stone-200 bg-stone-50' : 'border-[#FDE7D8] bg-[#FFF8F3]'}`} data-testid={`parent-goal-${g.id}`}>
      <div className="flex items-center gap-2 mb-1"><GoalBadge source="parent" />{paused && <span className="text-[11px] text-stone-500">Paused</span>}</div>
      {editing ? (
        <div className="flex gap-2">
          <input value={title} onChange={(e) => setTitle(e.target.value)} className="flex-1 border rounded-xl px-3 py-1.5 text-sm" data-testid={`parent-goal-edit-input-${g.id}`} />
          <button onClick={async () => { if (await onPatch(g, { title })) setEditing(false); }} className="p-1.5 text-emerald-700" aria-label="Save" data-testid={`parent-goal-save-${g.id}`}><Check className="w-4 h-4" /></button>
          <button onClick={() => { setEditing(false); setTitle(g.title); }} className="p-1.5 text-stone-500" aria-label="Cancel"><X className="w-4 h-4" /></button>
        </div>
      ) : <p className="text-sm text-stone-900">{g.title}</p>}
      {g.review_due && (
        <div className="mt-2 text-xs text-[#5b4f7a] bg-[#7C6F9C]/10 rounded-xl p-2" data-testid={`parent-goal-review-${g.id}`}>
          You&apos;ve been focusing on this for a while! Keep going, choose something new, or mark it as achieved?
          <button onClick={() => onPatch(g, { reviewed: true })} className="ml-2 underline" data-testid={`parent-goal-keep-${g.id}`}>Keep going</button>
        </div>
      )}
      {!editing && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          <button onClick={() => setEditing(true)} className="text-xs px-2.5 py-1 rounded-full border border-stone-200 bg-white flex items-center gap-1" data-testid={`parent-goal-edit-${g.id}`}><Pencil className="w-3 h-3" /> Edit</button>
          <button onClick={() => onPatch(g, { status: paused ? 'active' : 'paused' })} className="text-xs px-2.5 py-1 rounded-full border border-stone-200 bg-white flex items-center gap-1" data-testid={`parent-goal-toggle-${g.id}`}>
            {paused ? <><Play className="w-3 h-3" /> Reactivate</> : <><Pause className="w-3 h-3" /> Pause</>}
          </button>
          <button onClick={() => onPatch(g, { status: 'achieved' })} className="text-xs px-2.5 py-1 rounded-full border border-emerald-200 bg-emerald-50 text-emerald-800 flex items-center gap-1" data-testid={`parent-goal-achieve-${g.id}`}><Trophy className="w-3 h-3" /> Achieved</button>
        </div>
      )}
    </li>
  );
}

function AddGoal({ childId, suggestions, onAdded, full }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [proposal, setProposal] = useState(null);
  const [busy, setBusy] = useState(false);

  const save = async (title, extra = {}) => {
    setBusy(true);
    try {
      await axios.post(`${API}/parent-app/children/${childId}/goals`, { title, ...extra }, auth());
      toast.success('Goal added'); setText(''); setProposal(null); setOpen(false); onAdded();
    } catch (e) { toast.error(errMsg(e, 'Could not add goal')); } finally { setBusy(false); }
  };
  const tidy = async () => {
    if (text.trim().length < 2) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/parent-app/children/${childId}/goals/tidy`, { text }, auth());
      setProposal(r.data.suggested);
    } catch { setProposal(text.trim()); } finally { setBusy(false); }
  };

  if (full) return <p className="text-xs text-stone-500 mt-3" data-testid="parent-goals-limit-msg">You have 3 active goals. Pause or mark one as achieved to add another.</p>;
  if (!open) return <button onClick={() => setOpen(true)} className={`${PFP.btnPrimary} mt-3 text-sm py-2 flex items-center gap-1`} data-testid="add-goal-btn"><Plus className="w-4 h-4" /> Add a Goal</button>;
  return (
    <div className="mt-3 space-y-3" data-testid="add-goal-panel">
      {suggestions.length > 0 && (
        <div>
          <p className="text-xs text-stone-500 mb-1.5">Suggestions for your child — tap to add</p>
          <div className="flex flex-wrap gap-1.5">
            {suggestions.map((s, i) => (
              <button key={s.title} disabled={busy} onClick={() => save(s.title, { from_suggestion: true })} className={`${PFP.chip} ${PFP.chipIdle} text-xs py-1.5 px-3`} title={s.reason} data-testid={`goal-suggestion-${i}`}>
                <Plus className="w-3 h-3 mr-1" />{s.title}
              </button>
            ))}
          </div>
        </div>
      )}
      <div>
        <p className="text-xs text-stone-500 mb-1.5">Or write your own, in your own words</p>
        <div className="flex gap-2">
          <input value={text} onChange={(e) => { setText(e.target.value); setProposal(null); }} placeholder="e.g. I want my child to start putting two words together" className="flex-1 border rounded-2xl px-3 py-2 text-sm" data-testid="custom-goal-input" />
          <button onClick={tidy} disabled={busy || text.trim().length < 2} className={`${PFP.btnPrimary} text-sm py-2 px-4`} data-testid="custom-goal-next-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Next'}</button>
        </div>
      </div>
      {proposal !== null && (
        <div className="rounded-2xl bg-[#7C6F9C]/10 p-3 space-y-2" data-testid="custom-goal-confirm">
          <p className="text-xs text-[#5b4f7a] flex items-center gap-1"><Sparkles className="w-3 h-3" /> Here&apos;s a short version — edit it if you like:</p>
          <input value={proposal} onChange={(e) => setProposal(e.target.value)} className="w-full border rounded-xl px-3 py-1.5 text-sm" data-testid="custom-goal-proposal-input" />
          <button onClick={() => save(proposal, { original_text: text })} disabled={busy || proposal.trim().length < 2} className={`${PFP.btnPrimary} text-sm py-2`} data-testid="custom-goal-save-btn">Save goal</button>
        </div>
      )}
      <button onClick={() => setOpen(false)} className={`${PFP.btnGhost} text-xs`} data-testid="add-goal-cancel-btn">Cancel</button>
    </div>
  );
}

export default function ParentAppGoals() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [childId, setChildId] = useState(params.get('child'));
  const [data, setData] = useState(null);

  const load = useCallback(async (cid) => {
    try {
      const r = await axios.get(`${API}/parent-app/children/${cid}/goals`, auth());
      setData(r.data);
    } catch (e) { toast.error(errMsg(e, 'Could not load goals')); }
  }, []);

  useEffect(() => {
    if (childId) { load(childId); return; }
    axios.get(`${API}/parent-app/children`, auth()).then((r) => {
      const first = (r.data || [])[0];
      if (first) setChildId(first.id);
    }).catch(() => {});
  }, [childId, load]);

  const patch = async (g, body) => {
    try {
      await axios.patch(`${API}/parent-app/children/${childId}/goals/${g.id}`, body, auth());
      await load(childId);
      return true;
    } catch (e) { toast.error(errMsg(e, 'Could not update goal')); return false; }
  };

  const active = (data?.parent_goals || []).filter((g) => g.status === 'active').length;

  return (
    <div className={PFP.page} data-testid="parent-goals-page">
      <div className="max-w-2xl mx-auto px-4 py-6 space-y-4">
        <button onClick={() => navigate('/parent-app/home')} className={`${PFP.btnGhost} -ml-3 flex items-center gap-1 text-sm`} data-testid="goals-back-btn"><ArrowLeft className="w-4 h-4" /> Home</button>
        <div>
          <h1 className={`${PFP.h2} flex items-center gap-2`}><Target className="w-6 h-6 text-[#7C6F9C]" /> My Child&apos;s Goals</h1>
          <p className="text-sm text-stone-600 mt-2 leading-relaxed">{INTRO}</p>
        </div>
        <HowGoalsShapePlay />
        {!data ? (
          <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-[#7C6F9C]" /></div>
        ) : (
          <>
            <TherapistGoals goals={data.therapist_goals} />
            <section className={`${PFP.card} p-5`} data-testid="parent-goals-section">
              <h2 className="font-serif text-xl text-[#3a2f4f] mb-1">Goals You&apos;ve Chosen</h2>
              <p className="text-xs text-stone-500 mb-3">Up to {data.max_active} active goals that matter to your family ({active}/{data.max_active} active).</p>
              {data.parent_goals.length === 0 && <p className="text-sm text-stone-600">You haven&apos;t chosen any goals yet.</p>}
              <ul className="space-y-2">{data.parent_goals.map((g) => <ParentGoalItem key={g.id} g={g} onPatch={patch} />)}</ul>
              <AddGoal childId={childId} suggestions={data.suggestions} onAdded={() => load(childId)} full={active >= data.max_active} />
              {data.achieved_goals.length > 0 && (
                <div className="mt-5" data-testid="achieved-goals">
                  <p className="text-xs uppercase tracking-wider text-stone-500 font-semibold mb-1.5">Achieved 🎉</p>
                  <ul className="space-y-1">
                    {data.achieved_goals.map((g) => (
                      <li key={g.id} className="text-sm text-stone-600 flex items-center gap-2" data-testid={`achieved-goal-${g.id}`}>
                        <Trophy className="w-3.5 h-3.5 text-emerald-600" />{g.title}
                        <span className="text-[11px] text-stone-400">{(g.achieved_at || '').slice(0, 10)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </section>
          </>
        )}
      </div>
    </div>
  );
}
